import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Lock, RefreshCw, Save, LogOut, AlertCircle, CheckCircle2 } from "lucide-react";
import Logo from "@/components/site/Logo";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "pg_admin_token_v1";

/**
 * Tiny, password-protected admin page for marketing-site operational settings.
 *
 * Why in-session only: the ADMIN_API_TOKEN is long-lived on the server but
 * we don't want it persisted in localStorage where a stolen laptop or a
 * malicious browser extension could lift it. sessionStorage dies when the
 * tab closes — that's the right trade-off for an infrequently-used tool.
 *
 * This page is intentionally NOT linked from the navbar or footer. Visit
 * /admin directly; unauthenticated users see only the token prompt.
 */
export default function Admin() {
    const [token, setToken] = useState(() => {
        try { return sessionStorage.getItem(TOKEN_KEY) || ""; } catch (e) { return ""; }
    });
    const [tokenInput, setTokenInput] = useState("");
    const [verifying, setVerifying] = useState(false);

    const authed = !!token;

    const handleUnlock = async (e) => {
        e?.preventDefault();
        if (!tokenInput.trim()) return;
        setVerifying(true);
        try {
            // Verify by attempting a read-with-auth probe — PUT with the
            // current rate (no-op) confirms the token works without making
            // a state change.
            const current = await axios.get(`${API}/api/settings/fx-rate`, { timeout: 8000 });
            const sameRate = Number(current.data?.rate) || 18;
            await axios.put(
                `${API}/api/settings/fx-rate`,
                { rate: sameRate },
                { headers: { Authorization: `Bearer ${tokenInput.trim()}` }, timeout: 8000 }
            );
            sessionStorage.setItem(TOKEN_KEY, tokenInput.trim());
            setToken(tokenInput.trim());
            toast.success("Unlocked");
        } catch (err) {
            const status = err.response?.status;
            if (status === 401 || status === 403) {
                toast.error("Invalid admin token.");
            } else {
                toast.error("Could not verify token. Check connection and try again.");
            }
        } finally {
            setVerifying(false);
        }
    };

    const signOut = () => {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setTokenInput("");
        toast("Signed out of admin");
    };

    return (
        <div data-testid="admin-page" className="min-h-screen bg-zinc-50 flex flex-col">
            <header className="bg-white border-b border-zinc-200 px-6 py-4 flex items-center justify-between">
                <Logo variant="light" />
                <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Admin</span>
                    {authed && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={signOut}
                            data-testid="admin-signout-btn"
                            className="text-zinc-600 hover:text-zinc-900"
                        >
                            <LogOut className="h-4 w-4 mr-1.5" /> Sign out
                        </Button>
                    )}
                </div>
            </header>

            <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-12">
                {!authed ? (
                    <UnlockCard
                        tokenInput={tokenInput}
                        setTokenInput={setTokenInput}
                        verifying={verifying}
                        onSubmit={handleUnlock}
                    />
                ) : (
                    <FxRatePanel token={token} />
                )}
            </main>
        </div>
    );
}

function UnlockCard({ tokenInput, setTokenInput, verifying, onSubmit }) {
    return (
        <form onSubmit={onSubmit} className="max-w-md mx-auto mt-10 rounded-2xl border border-zinc-200 bg-white p-8 shadow-sm">
            <div className="h-10 w-10 rounded-xl bg-zinc-100 flex items-center justify-center mb-5">
                <Lock className="h-5 w-5 text-zinc-700" />
            </div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-zinc-900">Admin sign in</h1>
            <p className="mt-2 text-sm text-zinc-600">Paste your admin API token to manage operational settings.</p>

            <label className="mt-6 block">
                <span className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Admin token</span>
                <Input
                    type="password"
                    value={tokenInput}
                    onChange={(e) => setTokenInput(e.target.value)}
                    placeholder="••••••••••••••••••••"
                    autoComplete="off"
                    data-testid="admin-token-input"
                    className="mt-2"
                />
            </label>

            <Button
                type="submit"
                disabled={verifying || !tokenInput.trim()}
                data-testid="admin-unlock-btn"
                className="mt-5 w-full bg-zinc-900 hover:bg-zinc-800 text-white h-11"
            >
                {verifying ? "Verifying..." : "Unlock"}
            </Button>

            <p className="mt-6 text-xs text-zinc-500 leading-relaxed">
                Your token is stored in this browser tab's session only — sign out or close the tab to clear it.
                Lost your token? Rotate it on the server and generate a new one.
            </p>
        </form>
    );
}

function FxRatePanel({ token }) {
    const [loading, setLoading] = useState(true);
    const [current, setCurrent] = useState(null);
    const [newRate, setNewRate] = useState("");
    const [saving, setSaving] = useState(false);
    const [adminUser, setAdminUser] = useState("");

    const refresh = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/settings/fx-rate`);
            setCurrent(res.data);
            if (!newRate) setNewRate(String(res.data.rate ?? ""));
        } catch (e) {
            toast.error("Could not load current rate.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = async (e) => {
        e.preventDefault();
        const parsed = Number(newRate);
        if (!Number.isFinite(parsed) || parsed <= 0 || parsed >= 1000) {
            toast.error("Rate must be a positive number below 1000.");
            return;
        }
        setSaving(true);
        try {
            const res = await axios.put(
                `${API}/api/settings/fx-rate`,
                { rate: parsed },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        ...(adminUser.trim() ? { "X-Admin-User": adminUser.trim() } : {}),
                    },
                }
            );
            toast.success(`Rate updated to R${res.data.rate} = $1`);
            setCurrent(res.data);
        } catch (err) {
            const status = err.response?.status;
            if (status === 401 || status === 403) {
                toast.error("Token rejected. Please sign out and re-enter.");
            } else if (status === 422) {
                toast.error("Validation failed — check the value.");
            } else {
                toast.error("Update failed. Try again.");
            }
        } finally {
            setSaving(false);
        }
    };

    const currentRate = current?.rate;
    const hasDiff = Number(newRate) !== Number(currentRate);
    const lastUpdated = current?.updated_at
        ? new Date(current.updated_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
        : "Never (default value in use)";

    return (
        <div className="space-y-6">
            <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-brand-primary">Display settings</p>
                <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-zinc-900">
                    ZAR → USD display rate
                </h1>
                <p className="mt-2 text-sm text-zinc-600 max-w-xl">
                    Controls the "billed at R… = $1" subtitle on the Pricing page. Paystack charges
                    in ZAR regardless; this is a display-only conversion for international visitors.
                </p>
            </div>

            {/* Current state card */}
            <div data-testid="admin-current-state" className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Current rate</p>
                        <p data-testid="admin-current-rate" className="mt-2 font-display text-4xl font-bold tracking-tight text-zinc-900">
                            {loading ? "…" : `R${currentRate} = $1`}
                        </p>
                        <p className="mt-2 text-xs text-zinc-500">
                            Last updated: <span data-testid="admin-updated-at">{loading ? "…" : lastUpdated}</span>
                            {current?.updated_by && <> · by {current.updated_by}</>}
                        </p>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={refresh}
                        disabled={loading}
                        data-testid="admin-refresh-btn"
                    >
                        <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
                    </Button>
                </div>
            </div>

            {/* Update form */}
            <form onSubmit={handleSave} className="rounded-2xl border border-zinc-200 bg-white p-6 shadow-sm">
                <h2 className="font-display text-lg font-semibold text-zinc-900">Set a new rate</h2>
                <p className="mt-1 text-sm text-zinc-600">
                    Enter the number of Rand per 1 USD. For example, if the ZAR strengthens to R16 = $1, enter <code className="font-mono-pg text-xs bg-zinc-100 px-1.5 py-0.5 rounded">16</code>.
                </p>

                <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <label className="sm:col-span-1">
                        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">New rate</span>
                        <div className="mt-2 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">R</span>
                            <Input
                                type="number"
                                step="0.01"
                                min="0.01"
                                max="999"
                                value={newRate}
                                onChange={(e) => setNewRate(e.target.value)}
                                data-testid="admin-new-rate-input"
                                className="pl-7 text-lg font-semibold tabular-nums"
                            />
                        </div>
                    </label>
                    <label className="sm:col-span-2">
                        <span className="text-xs font-semibold uppercase tracking-[0.14em] text-zinc-500">Your name (optional, for audit log)</span>
                        <Input
                            value={adminUser}
                            onChange={(e) => setAdminUser(e.target.value)}
                            placeholder="e.g. founder, ops-katia"
                            data-testid="admin-user-input"
                            className="mt-2"
                        />
                    </label>
                </div>

                {hasDiff && !loading && (
                    <div className="mt-5 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50/70 px-3.5 py-2.5">
                        <AlertCircle className="h-4 w-4 mt-0.5 text-amber-600 shrink-0" />
                        <p className="text-[13px] text-amber-900">
                            You're about to change the rate from <strong>R{currentRate}</strong> to <strong>R{newRate}</strong> per $1.
                            The pricing page subtitle will update on next page load for all visitors.
                        </p>
                    </div>
                )}

                <div className="mt-5 flex items-center gap-3">
                    <Button
                        type="submit"
                        disabled={saving || !hasDiff || loading}
                        data-testid="admin-save-btn"
                        className="bg-brand-primary hover:bg-brand-hover text-white h-11 px-5"
                    >
                        {saving ? (
                            <>Saving…</>
                        ) : (
                            <>
                                <Save className="h-4 w-4 mr-1.5" /> Save new rate
                            </>
                        )}
                    </Button>
                    {!hasDiff && !loading && (
                        <span className="inline-flex items-center gap-1.5 text-xs text-zinc-500">
                            <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Matches current rate
                        </span>
                    )}
                </div>
            </form>

            <div className="rounded-xl bg-brand-tint/50 border border-brand-muted p-5 text-sm text-brand-primary leading-relaxed">
                <p className="font-semibold">⚠ Important reminder</p>
                <p className="mt-1">
                    This tool updates the <em>display subtitle</em> only. The headline USD prices on the pricing cards
                    ($72, $250, etc.) are stored as fixed values and <strong>do not auto-recalculate</strong>.
                    If the rate moves materially (e.g. more than ~5%), also review and update those values in{" "}
                    <code className="font-mono-pg text-xs bg-white border border-brand-accent-muted px-1.5 py-0.5 rounded">PricingTable.jsx</code>.
                </p>
            </div>
        </div>
    );
}
